import React, { useState, useEffect } from "react";
import { MdArrowForward } from "react-icons/md";

import styles from "./DeliveryTimeSelector.module.scss";

interface Props {
  displayText: string;
  pickerVisible: boolean;
  minimumDate: Date | null;
  onPress: () => void;
  onClose: () => void;
  onSelect: (date?: Date) => void;
  isTimeAvailable: (date: Date) => boolean;
}

const isToday = (date: Date): boolean => {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const formatLocalDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const DeliveryTimeSelector: React.FC<Props> = ({
  displayText,
  pickerVisible,
  minimumDate,
  onPress,
  onClose,
  onSelect,
  isTimeAvailable,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(minimumDate);
  const [isValid, setIsValid] = useState<boolean>(true);

  useEffect(() => {
    setSelectedDate(minimumDate);
  }, [minimumDate]);

  useEffect(() => {
    if (!selectedDate) {
      setIsValid(false);
      return;
    }
    setIsValid(isTimeAvailable(selectedDate));
  }, [selectedDate, isTimeAvailable]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(new Date(e.target.value));
  };

  const handleConfirm = () => {
    if (isValid && selectedDate) {
      onSelect(selectedDate);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedDate(minimumDate);
    setIsValid(minimumDate ? isTimeAvailable(minimumDate) : false);
    onClose();
  };

  const showTomorrowWarning = !!minimumDate && !isToday(minimumDate);

  return (
    <div className={styles.Container}>
      <span className={styles.Label}>Время получения</span>

      {showTomorrowWarning && (
        <p className={styles.Warning}>Заказ на сегодня невозможен</p>
      )}

      <button type="button" className={styles.Selector} onClick={onPress}>
        <span className={styles.Text}>{displayText}</span>
        <MdArrowForward size={18} />
      </button>

      {pickerVisible && minimumDate && selectedDate && (
        <div className={styles.Overlay} onClick={onClose}>
          <div className={styles.Modal} onClick={(e) => e.stopPropagation()}>
            <h4 className={styles.Title}>Выберите время получения</h4>

            <input
              type="datetime-local"
              className={styles.Input}
              value={formatLocalDateTime(selectedDate)}
              min={formatLocalDateTime(minimumDate)}
              onChange={handleDateChange}
            />

            {!isValid && (
              <p className={styles.Error}>
                В это время мы не работаем. Выберите другое время.
              </p>
            )}

            <div className={styles.Actions}>
              <button
                type="button"
                className={styles.Cancel}
                onClick={handleCancel}
              >
                Отмена
              </button>
              <button
                type="button"
                className={styles.Confirm}
                onClick={handleConfirm}
                disabled={!isValid}
              >
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
